/**
 * Tipos e chamadas do turno de caixa.
 *
 * O contrato espelha `api/internal/modulos/caixa/dominio.go` e
 * `docs/plano/turno-de-caixa.md`. Três pontos carregam o comportamento da tela:
 *
 * 1. `GET /caixa/turnos/atual` responde **200 com `turno: null`** quando a loja
 *    ainda não abriu o caixa. Não é 404 — a tela precisa distinguir "não abriu"
 *    de "não carregou", e um 404 embaralharia as duas.
 * 2. O sinal do movimento vem do SERVIDOR, no campo `entra`. A tela não
 *    reimplementa "sangria é saída": se um tipo novo aparecer, o extrato
 *    acerta o sinal sem release do front.
 * 3. Dinheiro é inteiro em centavos em todo o caminho (CONSTITUICAO.md §1). A
 *    única conta que este arquivo faz é a soma da contagem por denominação, em
 *    aritmética inteira, e ela existe para a tela mostrar o total antes de
 *    enviar — quem grava o contado continua sendo o servidor, a partir das
 *    mesmas linhas.
 */
import { api } from './api'
import type { Centavos } from './dinheiro'

export type TipoMovimento = 'abertura' | 'venda' | 'sangria' | 'suprimento' | 'devolucao'

export type StatusTurno = 'aberto' | 'fechado'

/**
 * As 7 cédulas e 5 moedas do real, em centavos — a mesma lista fechada de
 * `caixa.DENOMINACOES` no Go.
 *
 * Da maior para a menor: é a ordem em que quem conta a gaveta empilha.
 */
export const DENOMINACOES: Centavos[] = [
  20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50, 25, 10, 5,
]

export type Movimento = {
  tipo: TipoMovimento
  tipo_rotulo: string
  valor_centavos: Centavos
  /** Verdadeiro quando o movimento SOMA na gaveta. Vem do servidor. */
  entra: boolean
  descricao?: string
  destino?: string
  venda_codigo?: string
  operador?: string
  autorizador?: string
  criado_em: string
  criado_em_hora: string
}

export type Contagem = {
  denominacao_centavos: Centavos
  quantidade: number
  total_centavos: Centavos
}

export type Turno = {
  codigo: string
  terminal: string
  status: StatusTurno

  aberto_em: string
  aberto_por?: string
  abertura_centavos: Centavos

  vendas_dinheiro_centavos: Centavos
  suprimentos_centavos: Centavos
  sangrias_centavos: Centavos
  devolucoes_centavos: Centavos
  /** O esperado. Derivado dos movimentos no turno aberto, gravado no fechado. */
  gaveta_centavos: Centavos

  cartao_pix_centavos: Centavos
  vendas_total_centavos: Centavos
  quantidade_vendas: number

  fechado_em?: string
  fechado_por?: string
  contado_centavos?: Centavos
  esperado_centavos?: Centavos
  diferenca_centavos?: number
  observacao?: string
}

export type TurnoAtual = {
  /** Nulo quando não há turno aberto — ver o ponto 1 no topo. */
  turno: Turno | null
  movimentos: Movimento[]
}

export type FichaTurno = {
  turno: Turno
  movimentos: Movimento[]
  contagem: Contagem[]
}

export type HistoricoTurnos = {
  linhas: Turno[]
  total: number
  pagina: number
  tamanho: number
}

export type LinhaContada = {
  denominacao_centavos: Centavos
  quantidade: number
}

/** Percentual em ponto-base: 1 pb = 0,01 %. 1000 = 10 % (CONSTITUICAO.md §1). */
export type Pontos = number

/**
 * Regras do balcão — espelha `api/internal/modulos/caixa/configuracao.go`
 * (migração 0018).
 *
 * Só `bloquear_venda_sem_turno` muda comportamento hoje: ligado, e sem turno
 * aberto na filial, a venda é recusada com 409 (ver `ehVendaSemTurno` em
 * `plataforma/vendas.ts`). Os outros três campos são salvos de verdade, mas
 * nenhum consumidor os lê ainda — ver o comentário no arquivo Go.
 */
export type ConfiguracaoBalcao = {
  desconto_maximo_pontos_base: Pontos
  limite_gaveta_centavos: Centavos
  prazo_troca_dias: number
  bloquear_venda_sem_turno: boolean
}

// ------------------------------------------------------------------ chamadas

export function carregarTurnoAtual(sinal?: AbortSignal): Promise<TurnoAtual> {
  return api.get<TurnoAtual>('/caixa/turnos/atual', sinal)
}

export function carregarHistorico(
  parametros: { status?: string; busca?: string; pagina?: number },
  sinal?: AbortSignal
): Promise<HistoricoTurnos> {
  const consulta = new URLSearchParams()
  if (parametros.status) consulta.set('status', parametros.status)
  if (parametros.busca) consulta.set('busca', parametros.busca)
  if (parametros.pagina && parametros.pagina > 1) consulta.set('pagina', String(parametros.pagina))

  const cauda = consulta.toString()
  return api.get<HistoricoTurnos>(`/caixa/turnos${cauda ? `?${cauda}` : ''}`, sinal)
}

export function carregarTurno(codigo: string, sinal?: AbortSignal): Promise<FichaTurno> {
  return api.get<FichaTurno>(`/caixa/turnos/${encodeURIComponent(codigo)}`, sinal)
}

export function abrirTurno(entrada: {
  abertura_centavos: Centavos
  terminal?: string
}): Promise<{ turno: Turno }> {
  return api.post<{ turno: Turno }>('/caixa/turnos', entrada)
}

export function lancarMovimento(
  codigo: string,
  entrada: {
    tipo: 'sangria' | 'suprimento'
    valor_centavos: Centavos
    destino?: string
    descricao?: string
  },
  pin?: string
): Promise<{ turno: Turno }> {
  return api.post<{ turno: Turno }>(
    `/caixa/turnos/${encodeURIComponent(codigo)}/movimentos`,
    entrada,
    pin
  )
}

export function fecharTurno(
  codigo: string,
  entrada: { contagem: LinhaContada[]; observacao?: string }
): Promise<{ turno: Turno }> {
  return api.post<{ turno: Turno }>(`/caixa/turnos/${encodeURIComponent(codigo)}/fechar`, entrada)
}

export function carregarConfiguracaoBalcao(sinal?: AbortSignal): Promise<ConfiguracaoBalcao> {
  return api.get<ConfiguracaoBalcao>('/caixa/configuracao', sinal)
}

export function salvarConfiguracaoBalcao(pedido: ConfiguracaoBalcao): Promise<ConfiguracaoBalcao> {
  return api.put<ConfiguracaoBalcao>('/caixa/configuracao', pedido)
}

// ------------------------------------------------------------------- contas

/**
 * Soma a contagem por denominação.
 *
 * Aritmética inteira, como manda o §1: `denominação × quantidade` nunca sai de
 * centavos. Existe para a tela mostrar o total enquanto a pessoa digita — o
 * número que vale é o que o servidor grava a partir das mesmas linhas.
 */
export function somarContagem(linhas: Record<number, number>): Centavos {
  return DENOMINACOES.reduce(
    (total, denominacao) => total + denominacao * (linhas[denominacao] ?? 0),
    0
  )
}
